'use client';

import { ClipData } from '@/components/ClipCard';

const STORAGE_KEY = 'cliphub_uploaded_clips';
const CLIP_STORE_EVENT = 'cliphub_clips_updated';

export function getStoredClips(): ClipData[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as ClipData[];
  } catch {
    return [];
  }
}

export function saveClip(clip: ClipData): void {
  if (typeof window === 'undefined') return;
  try {
    const existing = getStoredClips();
    // Prepend so newest appears first
    const updated = [clip, ...existing];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    // Notify all listeners (same tab)
    window.dispatchEvent(new CustomEvent(CLIP_STORE_EVENT));
  } catch {
    // ignore storage errors
  }
}

export function saveClips(clips: ClipData[]): void {
  if (typeof window === 'undefined') return;
  try {
    const existing = getStoredClips();
    const updated = [...clips, ...existing];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent(CLIP_STORE_EVENT));
  } catch {
    // ignore storage errors
  }
}

export function onClipsUpdated(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(CLIP_STORE_EVENT, callback);
  return () => window.removeEventListener(CLIP_STORE_EVENT, callback);
}

export function deleteClip(clipId: string): void {
  if (typeof window === 'undefined') return;
  try {
    const existing = getStoredClips();
    const updated = existing.filter((c) => c.id !== clipId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent(CLIP_STORE_EVENT));
  } catch {
    // ignore storage errors
  }
}

export function generateClipId(): string {
  return `clip_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

// ─── Saved Clips Store ────────────────────────────────────────────────────────

const SAVED_KEY = 'cliphub_saved_clips';
const SAVED_EVENT = 'cliphub_saved_updated';

export function getSavedClips(): ClipData[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(SAVED_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as ClipData[];
  } catch {
    return [];
  }
}

export function isClipSaved(clipId: string): boolean {
  return getSavedClips().some((c) => c.id === clipId);
}

export function toggleSavedClip(clip: ClipData): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const existing = getSavedClips();
    const alreadySaved = existing.some((c) => c.id === clip.id);
    const updated = alreadySaved
      ? existing.filter((c) => c.id !== clip.id)
      : [clip, ...existing];
    localStorage.setItem(SAVED_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent(SAVED_EVENT));
    return !alreadySaved;
  } catch {
    return false;
  }
}

export function onSavedClipsUpdated(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(SAVED_EVENT, callback);
  return () => window.removeEventListener(SAVED_EVENT, callback);
}
